import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  Font,
} from '@react-pdf/renderer'
import type { CV, DateFormat, CVLanguage } from '../../types/cv'
import { creativePalettes, type CreativeColors } from '../../utils/colorPalettes'
import { getCVLabels, getLevelLabel } from '../../utils/cvTranslations'
import { EmailIcon, PhoneIcon, LocationIcon, BirthDateIcon } from './ContactIcons'

function getColors(paletteId?: string): CreativeColors {
  return creativePalettes[paletteId || 'indigo'] || creativePalettes.indigo
}

// Register Poppins font
Font.register({
  family: 'Poppins',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJA.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1s.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1s.ttf',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1s.ttf',
      fontWeight: 700,
    },
  ],
})


// Color palette matching the preview
const INDIGO = '#6366f1'
const INDIGO_LIGHT = '#e0e7ff'
const PURPLE = '#a855f7'
const PURPLE_LIGHT = '#f3e8ff'
const PINK = '#ec4899'
const PINK_LIGHT = '#fce7f3'
const DARK_COLOR = '#1e1b4b'
const GRAY_COLOR = '#64748b'
const LIGHT_GRAY = '#f1f5f9'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Poppins',
    fontSize: 10,
    color: DARK_COLOR,
    lineHeight: 1.5,
    paddingTop: 25,
  },
  // Top accent bar - simulated gradient with 3 sections (fixed to top edge)
  accentBarContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    height: 10,
  },
  accentBarIndigo: {
    flex: 1,
    backgroundColor: INDIGO,
  },
  accentBarPurple: {
    flex: 1,
    backgroundColor: PURPLE,
  },
  accentBarPink: {
    flex: 1,
    backgroundColor: PINK,
  },
  container: {
    padding: 35,
    paddingTop: 20,
    flex: 1,
  },
  // Header with colored background
  header: {
    backgroundColor: LIGHT_GRAY,
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flex: 1,
  },
  headerPhoto: {
    width: 90,
    height: 90,
    borderRadius: 12,
    objectFit: 'cover',
  },
  name: {
    fontSize: 26,
    fontWeight: 700,
    color: INDIGO,
    marginBottom: 4,
  },
  title: {
    fontSize: 13,
    color: GRAY_COLOR,
    fontWeight: 600,
    marginBottom: 12,
  },
  contactGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  contactItemIndigo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: INDIGO_LIGHT,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    fontSize: 8,
    color: INDIGO,
    textDecoration: 'none',
  },
  contactItemPurple: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: PURPLE_LIGHT,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    fontSize: 8,
    color: PURPLE,
    textDecoration: 'none',
  },
  contactItemPink: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: PINK_LIGHT,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    fontSize: 8,
    color: PINK,
  },
  contactItemGray: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: LIGHT_GRAY,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    fontSize: 8,
    color: GRAY_COLOR,
  },
  contactIcon: {
    width: 12,
    height: 12,
    marginRight: 6,
  },
  // Section styles
  section: {
    marginBottom: 18,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionIconIndigo: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: INDIGO,
    marginRight: 10,
  },
  sectionIconPurple: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: PURPLE,
    marginRight: 10,
  },
  sectionIconPink: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: PINK,
    marginRight: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: DARK_COLOR,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  // Summary
  summaryText: {
    fontSize: 10,
    color: GRAY_COLOR,
    lineHeight: 1.6,
  },
  // Experience & Education entries
  entryContainer: {
    marginBottom: 14,
  },
  entryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  entryTitleRow: {
    flex: 1,
  },
  entryTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: DARK_COLOR,
  },
  entrySubtitleIndigo: {
    fontSize: 10,
    color: INDIGO,
    fontWeight: 500,
  },
  entrySubtitlePurple: {
    fontSize: 10,
    color: PURPLE,
    fontWeight: 500,
  },
  entryMeta: {
    alignItems: 'flex-end',
  },
  entryDate: {
    fontSize: 8,
    color: GRAY_COLOR,
    backgroundColor: LIGHT_GRAY,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  entryLocation: {
    fontSize: 8,
    color: GRAY_COLOR,
    marginTop: 2,
  },
  entryDescription: {
    fontSize: 9,
    color: GRAY_COLOR,
    marginTop: 6,
    lineHeight: 1.5,
  },
  // Skills
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  skillCategory: {
    marginBottom: 10,
  },
  skillCategoryTitle: {
    fontSize: 9,
    fontWeight: 600,
    color: DARK_COLOR,
    marginBottom: 6,
  },
  skillTagIndigo: {
    backgroundColor: INDIGO_LIGHT,
    color: INDIGO,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 8,
    fontWeight: 500,
  },
  skillTagPurple: {
    backgroundColor: PURPLE_LIGHT,
    color: PURPLE,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 8,
    fontWeight: 500,
  },
  skillTagPink: {
    backgroundColor: PINK_LIGHT,
    color: PINK,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 8,
    fontWeight: 500,
  },
  // Languages
  languagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  languageItem: {
    backgroundColor: INDIGO_LIGHT,
    borderRadius: 8,
    padding: 10,
    minWidth: 80,
    borderWidth: 1,
    borderColor: '#c7d2fe',
  },
  languageName: {
    fontSize: 10,
    fontWeight: 600,
    color: DARK_COLOR,
    marginBottom: 2,
  },
  languageLevel: {
    fontSize: 8,
    color: INDIGO,
    fontWeight: 500,
  },
  // Links
  linksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
})

const localeMap: Record<CVLanguage, string> = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  pt: 'pt-PT',
  de: 'de-DE',
}

function formatDate(
  dateStr: string | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  if (!dateStr) return ''

  const str = dateStr.trim().toLowerCase()
  const locale = localeMap[language] || 'en-US'

  if (str.includes('present') || str.includes('actual') || str.includes('current')) {
    return presentLabel
  }

  const yearOnly = format === 'year-only'

  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-')
    if (yearOnly) return year
    const date = new Date(parseInt(year), parseInt(month) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    if (yearOnly) return dateStr.split('-')[0]
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  if (/^\d{4}$/.test(dateStr)) {
    return dateStr
  }

  const mmYYYYMatch = dateStr.match(/^(\d{1,2})[/-](\d{4})$/)
  if (mmYYYYMatch) {
    if (yearOnly) return mmYYYYMatch[2]
    const date = new Date(parseInt(mmYYYYMatch[2]), parseInt(mmYYYYMatch[1]) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/)
  if (yearMatch) {
    return yearMatch[0]
  }

  return ''
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  current: boolean | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  const startStr = formatDate(start, format, language, presentLabel)
  const endStr = current ? presentLabel : formatDate(end, format, language, presentLabel)
  if (!startStr && !endStr) return ''
  if (!startStr) return endStr
  if (!endStr) return startStr
  return `${startStr} - ${endStr}`
}


interface CreativeTemplateProps {
  cv: CV
  colorPalette?: string
}


export function CreativeTemplate({ cv, colorPalette }: CreativeTemplateProps) {
  const paletteColors = getColors(colorPalette)
  const labels = getCVLabels(cv.settings?.language, cv.settings?.customLabels)
  const showIcons = cv.settings?.showContactIcons ?? true
  const hasPersonalInfo =
    cv.personalInfo?.fullName ||
    cv.personalInfo?.title ||
    cv.personalInfo?.email ||
    cv.personalInfo?.phone ||
    cv.personalInfo?.location

  const hasExperience = cv.experience && cv.experience.length > 0
  const hasEducation = cv.education && cv.education.length > 0
  const hasSkills =
    (cv.skills?.technical && cv.skills.technical.length > 0) ||
    (cv.skills?.soft && cv.skills.soft.length > 0) ||
    (cv.skills?.tools && cv.skills.tools.length > 0)
  const hasLanguages = cv.languages && cv.languages.some((lang) => lang.name)

  return (
    <Document
      title={`CV - ${cv.personalInfo?.fullName || 'Curriculum Vitae'}`}
      author={cv.personalInfo?.fullName || ''}
      subject={cv.personalInfo?.title || 'Curriculum Vitae'}
      creator="CVtoday"
      keywords={`CV, resume, ${cv.personalInfo?.fullName || ''}, ${cv.personalInfo?.title || ''}`}
    >
      <Page size="A4" style={styles.page}>
        {/* Top accent bar - simulated gradient (fixed, repeats on every page) */}
        <View style={styles.accentBarContainer} fixed>
          <View style={[styles.accentBarIndigo, { backgroundColor: paletteColors.accent1 }]} />
          <View style={[styles.accentBarPurple, { backgroundColor: paletteColors.accent2 }]} />
          <View style={[styles.accentBarPink, { backgroundColor: paletteColors.accent3 }]} />
        </View>

        <View style={styles.container}>
          {/* Header */}
          {hasPersonalInfo && (
            <View style={styles.header}>
              <View style={styles.headerContent}>
                <View style={styles.headerLeft}>
                  {cv.personalInfo?.fullName && (
                    <Text style={[styles.name, { color: paletteColors.accent1 }]}>{cv.personalInfo.fullName}</Text>
                  )}
                  {cv.personalInfo?.title && (
                    <Text style={styles.title}>{cv.personalInfo.title}</Text>
                  )}
                  <View style={styles.contactGrid}>
                    {cv.personalInfo?.email && (
                      <Link src={`mailto:${cv.personalInfo.email}`} style={[styles.contactItemIndigo, { backgroundColor: paletteColors.accent1Light }]}>
                        {showIcons && <EmailIcon size={9} color={paletteColors.accent1} style={{ marginRight: 4 }} />}
                        <Text style={{ color: paletteColors.accent1 }}>{cv.personalInfo.email}</Text>
                      </Link>
                    )}
                    {cv.personalInfo?.phone && (
                      <Link src={`tel:${cv.personalInfo.phone}`} style={[styles.contactItemPurple, { backgroundColor: paletteColors.accent2Light }]}>
                        {showIcons && <PhoneIcon size={9} color={paletteColors.accent2} style={{ marginRight: 4 }} />}
                        <Text style={{ color: paletteColors.accent2 }}>{cv.personalInfo.phone}</Text>
                      </Link>
                    )}
                    {cv.personalInfo?.location && (
                      <View style={[styles.contactItemPink, { backgroundColor: paletteColors.accent3Light }]}>
                        {showIcons && <LocationIcon size={9} color={paletteColors.accent3} style={{ marginRight: 4 }} />}
                        <Text style={{ color: paletteColors.accent3 }}>{cv.personalInfo.location}</Text>
                      </View>
                    )}
                    {cv.personalInfo?.birthDate && (
                      <View style={[styles.contactItemPink, { backgroundColor: paletteColors.accent3Light }]}>
                        {showIcons && <BirthDateIcon size={9} color={paletteColors.accent3} style={{ marginRight: 4 }} />}
                        <Text style={{ color: paletteColors.accent3 }}>{cv.personalInfo.birthDate}</Text>
                      </View>
                    )}
                  </View>
                  {cv.personalInfo?.links && cv.personalInfo.links.length > 0 && (
                    <View style={styles.linksContainer}>
                      {cv.personalInfo.links.map(
                        (link) =>
                          link.url ? (
                            <Link key={link.id} src={link.url} style={styles.contactItemGray}>
                              <Text>{link.label || link.url}</Text>
                            </Link>
                          ) : null
                      )}
                    </View>
                  )}
                </View>
                {cv.personalInfo?.photo && (
                  <Image src={cv.personalInfo.photo} style={styles.headerPhoto} />
                )}
              </View>
            </View>
          )}

          {/* Summary */}
          {cv.summary && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} minPresenceAhead={20}>
                <View style={[styles.sectionIconIndigo, { backgroundColor: paletteColors.accent1 }]} />
                <Text style={styles.sectionTitle}>{labels.summary}</Text>
              </View>
              <Text style={styles.summaryText}>{cv.summary}</Text>
            </View>
          )}

          {/* Experience */}
          {hasExperience && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} minPresenceAhead={40}>
                <View style={[styles.sectionIconPurple, { backgroundColor: paletteColors.accent2 }]} />
                <Text style={styles.sectionTitle}>{labels.experience}</Text>
              </View>
              {cv.experience?.map((exp) => (
                <View key={exp.id} style={styles.entryContainer}>
                  <View style={styles.entryHeader}>
                    <View style={styles.entryTitleRow}>
                      <Text style={styles.entryTitle}>
                        {exp.position || 'Puesto'}
                      </Text>
                      {exp.company && (
                        <Text style={[styles.entrySubtitleIndigo, { color: paletteColors.accent1 }]}>{exp.company}</Text>
                      )}
                    </View>
                    <View style={styles.entryMeta}>
                      <Text style={styles.entryDate}>
                        {formatDateRange(exp.startDate, exp.endDate, exp.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                      </Text>
                      {exp.location && (
                        <Text style={styles.entryLocation}>{exp.location}</Text>
                      )}
                    </View>
                  </View>
                  {exp.description && (
                    <Text style={styles.entryDescription}>{exp.description}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Education */}
          {hasEducation && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} minPresenceAhead={40}>
                <View style={[styles.sectionIconPink, { backgroundColor: paletteColors.accent3 }]} />
                <Text style={styles.sectionTitle}>{labels.education}</Text>
              </View>
              {cv.education?.map((edu) => (
                <View key={edu.id} style={styles.entryContainer}>
                  <View style={styles.entryHeader}>
                    <View style={styles.entryTitleRow}>
                      <Text style={styles.entryTitle}>
                        {edu.degree}
                        {edu.field && ` en ${edu.field}`}
                      </Text>
                      {edu.institution && (
                        <Text style={[styles.entrySubtitlePurple, { color: paletteColors.accent2 }]}>{edu.institution}</Text>
                      )}
                    </View>
                    <View style={styles.entryMeta}>
                      <Text style={styles.entryDate}>
                        {formatDateRange(edu.startDate, edu.endDate, edu.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                      </Text>
                      {edu.location && (
                        <Text style={styles.entryLocation}>{edu.location}</Text>
                      )}
                    </View>
                  </View>
                  {edu.gpa && (
                    <Text style={styles.entryDescription}>{labels.gpa}: {edu.gpa}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Skills */}
          {hasSkills && (
            <View style={styles.section} wrap={false}>
              <View style={styles.sectionHeader}>
                <View style={[styles.sectionIconIndigo, { backgroundColor: paletteColors.accent1 }]} />
                <Text style={styles.sectionTitle}>{labels.skills}</Text>
              </View>
              <View style={styles.skillsContainer}>
                {/* Combine all skills with alternating colors */}
                {[
                  ...(cv.skills?.technical || []),
                  ...(cv.skills?.soft || []),
                  ...(cv.skills?.tools || []),
                ].map((skill, index) => (
                  <Text
                    key={index}
                    style={[
                      styles.skillTagIndigo,
                      index % 3 === 0
                        ? { backgroundColor: paletteColors.accent1Light, color: paletteColors.accent1 }
                        : index % 3 === 1
                          ? { backgroundColor: paletteColors.accent2Light, color: paletteColors.accent2 }
                          : { backgroundColor: paletteColors.accent3Light, color: paletteColors.accent3 }
                    ]}
                  >
                    {skill}
                  </Text>
                ))}
              </View>
            </View>
          )}

          {/* Languages */}
          {hasLanguages && (
            <View style={styles.section} wrap={false}>
              <View style={styles.sectionHeader}>
                <View style={[styles.sectionIconPurple, { backgroundColor: paletteColors.accent2 }]} />
                <Text style={styles.sectionTitle}>{labels.languages}</Text>
              </View>
              <View style={styles.languagesContainer}>
                {cv.languages?.map(
                  (lang) =>
                    lang.name ? (
                      <View key={lang.id} style={[styles.languageItem, { backgroundColor: paletteColors.accent1Light, borderColor: paletteColors.accent1 }]}>
                        <Text style={styles.languageName}>{lang.name}</Text>
                        {lang.level ? (
                          <Text style={[styles.languageLevel, { color: paletteColors.accent1 }]}>
                            {getLevelLabel(lang.level, cv.settings?.language)}
                          </Text>
                        ) : null}
                      </View>
                    ) : null
                )}
              </View>
            </View>
          )}
        </View>
      </Page>
    </Document>
  )
}
